import { graphConfig } from "./authConfig";
import { CONFIG } from "src/config";

// Samenvatting van benodigde scopes
// User.Read: Toegang tot profielinformatie van de ingelogde gebruiker.
// Files.Read: Lezen van bestanden en mappen in de OneDrive van de gebruiker.
// Files.ReadWrite: Lezen, schrijven, aanmaken en verwijderen van bestanden en mappen.
// Files.Read.All: Toegang tot alle bestanden, inclusief gedeelde bestanden.
// Files.ReadWrite.All: Volledige toegang tot alle bestanden, inclusief de mogelijkheid om ze te delen.

/**
 * Attaches a given access token to a MS Graph API call. Returns information about the user
 * @param accessToken 
 */
export async function callMsGraph(accessToken) {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);

    const options = {
        method: "GET",
        headers: headers
    };

    return fetch(graphConfig.graphMeEndpoint, options)
        .then(response => response.json())
        .catch(error => console.log(error));
}

export async function getOneDriveFiles(accessToken) {
    const response = await fetch('https://graph.microsoft.com/v1.0/me/drive/root/children', {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
  
    if (!response.ok) {
      throw new Error('Failed to fetch OneDrive files');
    }
  
    const data = await response.json();
    return data;
}

export async function getDriveItems(accessToken, itemId) {
    const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${itemId}/children`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
  
    if (!response.ok) {
      throw new Error('Failed to fetch OneDrive files');
    }
  
    const data = await response.json();
    return data;
}

export async function getSharedDriveItems(accessToken, userId, itemId) {
    const response = await fetch(`https://graph.microsoft.com/v1.0/users/${userId}/drive/items/${itemId}/children`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
  
    if (!response.ok) {
      throw new Error('Failed to fetch OneDrive files');
    }
  
    const data = await response.json();
    const items = data.value;
    const sharedItems = [];
  
    for (const item of items) {
      if (item.folder) {
        // If the item is a folder, fetch its contents recursively
        const folderContents = await getSharedDriveItems(accessToken, userId, item.id);
        sharedItems.push(...folderContents);
      } else {
        // If the item is not a folder, add it to the list of shared items
        sharedItems.push(item);
      }
    }
  
    return sharedItems;
}

export async function uploadFileToOneDrive(accessToken, file, folderId) {
  const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${folderId}:/${file.name}:/content`, {
      method: 'PUT',
      headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': file.type
      },
      body: file
  });

  if (!response.ok) {
      throw new Error('Failed to upload file to OneDrive');
  }

  return await response.json();
}

export async function getFileContentAsBase64(accessToken, itemId) {
  const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${itemId}/content`, {
      headers: {
          'Authorization': `Bearer ${accessToken}`
      }
  });

  if (!response.ok) {
      throw new Error('Failed to download file content');
  }

  const fileContent = await response.blob();
  return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
          const base64String = reader.result.toString().replace(/^data:.+;base64,/, '');
          resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(fileContent);
  });
}

export async function deleteItemFromOneDrive(accessToken, itemId) {
    const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${itemId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${accessToken}`
        }
    });

    let data = {}
    try { 
        data = await response.json()
    } catch (e) { return true }

    if(!response.ok){ throw new Error(data.error.message) }

    return true
}

export async function createFolderInOneDrive(accessToken, parentId, folderName) {
  const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${parentId}/children`, {
      method: 'POST',
      headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
      },
      body: JSON.stringify({
          name: folderName,
          folder: {},
          '@microsoft.graph.conflictBehavior': 'rename'
      })
  });

  if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to create folder: ${error.error.message}`);
  }

  const folder = await response.json();
  console.log('Folder created successfully', folder);
  return folder;
}

export async function getUsers(accessToken) {
  const response = await fetch(`https://graph.microsoft.com/v1.0/users`, {
      headers: {
          'Authorization': `Bearer ${accessToken}`
      }
  });

  if (!response.ok) {
      throw new Error('Failed to fetch users');
  }

  const data = await response.json();
  return data.value; // This will return an array of user objects
}

export async function shareDocumentWithUsers(accessToken, itemId, userEmails) {
  const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${itemId}/invite`, {
      method: 'POST',
      headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
      },
      body: JSON.stringify({
          requireSignIn: true,
          sendInvitation: true,
          roles: ["read"], // or ["write"] depending on the required permission
          recipients: userEmails.map(email => ({ email }))
      })
  });

  if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to share document: ${error.error.message}`);
  }

  const data = await response.json();
  console.log('Share response:', data);
  return data;
}

export async function getDrivePath(accessToken, itemId) {
    const headers = {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
    };

    // Fetch the item to get its parent reference
    const fetchItem = async (id) => {
        const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${id}`, { headers });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Failed to get item: ${error.error.message}`);
        }
        return response.json();
    };

    const buildPath = async (item) => {
        const path = [];
        let currentItem = item;

        while (currentItem && currentItem.parentReference && currentItem.parentReference.id) {
            path.unshift(currentItem);
            currentItem = await fetchItem(currentItem.parentReference.id);
        }

        // Add root item if exists
        if (currentItem) {
            path.unshift(currentItem);
        }

        return path;
    };

    // Get the initial item
    const item = await fetchItem(itemId);

    // Build the path from the root to the item
    const path = await buildPath(item);

    return path;
}

export async function getSharedDrivePath(accessToken, userId, itemId) {
    const headers = {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
    };

    // Fetch the item to get its parent reference
    const fetchItem = async (id) => {
        const response = await fetch(`https://graph.microsoft.com/v1.0/users/${userId}/drive/items/${id}`, { headers });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Failed to get item: ${error.error.message}`);
        }
        return response.json();
    };

    const buildPath = async (item) => {
        const path = [];
        let currentItem = item;

        while (currentItem && currentItem.parentReference && currentItem.parentReference.id) {
            path.unshift(currentItem);
            try {
                currentItem =  await fetchItem(currentItem.parentReference.id);
            }catch(e){ 
                path.unshift({id:currentItem.parentReference.id, name:currentItem.parentReference.name});
                break;
            } 
        }

        return path;
    };

    // Get the initial item
    const item = await fetchItem(itemId);

    // Build the path from the root to the item
    const path = await buildPath(item);

    return path;
}

export async function createFolder(accessToken, itemId, name) {
    const endpoint = `https://graph.microsoft.com/v1.0/me/drive/items/${itemId}/children`;

    const requestBody = {
        "name": name,
        "folder": {},
        "@microsoft.graph.conflictBehavior": "rename"
    };

    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        const errorDetails = await response.text();
        throw new Error(`Failed to create folder. Status: ${response.status}, Details: ${errorDetails}`);
    }

    const data = await response.json();
    return data; // This will return the created folder object
}

export async function getSharingActivities(accessToken, itemId) {
    const endpoint = `https://graph.microsoft.com/v1.0/me/drive/items/${itemId}/activities`;

    try {
        const response = await fetch(endpoint, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) {
            const errorDetails = await response.text();
            return []
            //throw new Error(`Failed to fetch sharing activities. Status: ${response.status}, Details: ${errorDetails}`);
        }

        const data = await response.json();
        return data.value; // This will return an array of activity objects
    } catch (e) { return []}
}

export async function getSharedSharingActivities(accessToken, userId, itemId) {
    const endpoint = `https://graph.microsoft.com/v1.0/users/${userId}/drive/items/${itemId}/activities`;

    try {
        const response = await fetch(endpoint, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) {
            const errorDetails = await response.text();
            return []
            //throw new Error(`Failed to fetch sharing activities. Status: ${response.status}, Details: ${errorDetails}`);
        }

        const data = await response.json();
        return data.value; // This will return an array of activity objects
    } catch (e) { return []}
}

export async function getSharedItems(accessToken) {
    const endpoint = 'https://graph.microsoft.com/v1.0/me/drive/sharedWithMe';

    const response = await fetch(endpoint, {
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        const errorDetails = await response.text();
        throw new Error(`Failed to fetch shared items. Status: ${response.status}, Details: ${errorDetails}`);
    }

    const data = await response.json();
    return data.value; // This will return an array of shared driveItem objects
}

export async function getDocumentsByNameSubstring(accessToken, substring, parentFolderId = null) {
    // Determine the base URL based on whether a parent folder ID is provided
    const baseUrl = parentFolderId 
        ? `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}/search(q='${substring}')`
        : `https://graph.microsoft.com/v1.0/me/drive/root/search(q='${substring}')`;

    // Make the API request
    const response = await fetch(baseUrl, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        }
    });

    // Check if the response is okay
    if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to retrieve documents: ${error.error.message}`);
    }

    // Parse the response data
    const data = await response.json();
    return data.value.filter(item => item.name.includes(substring));
}

export async function getDocumentEditLink(accessToken, itemId) {
    const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${itemId}/createLink`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            type: 'edit', // Type can be 'view', 'edit', or 'embed'
            scope: 'anonymous'
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to get edit link: ${error.error.message}`);
    }

    const data = await response.json();

    return data.link.webUrl; // This is the URL you can use in an iframe
}

export async function getDocumentPreview(accessToken, itemId) {
    const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${itemId}/preview`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            allowEdit: true
        })
    });

  
    if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to get preview document: ${error.error.message}`);
    }
  
    const data = await response.json();
    return data;
}

export async function createEmptyFile(accessToken, fileType, folderId) {
    // Define the base URL for OneDrive
    const baseUrl = `https://graph.microsoft.com/v1.0/me/drive/items/${folderId}/children`;

    // Define the file extension and content type
    const fileExtensions = {
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        onetoc: 'application/onenote',
        vsdx: 'application/vnd.visio',
        clipchamp: 'video/mp4' // Assuming Clipchamp deals with MP4 video files
    };

    // Validate the file type
    if (!fileExtensions[fileType]) {
        throw new Error('Invalid file type specified. Valid types are docx, pptx, xlsx, onetoc, vsdx, clipchamp.');
    }

    // Define the file name
    const fileName = `Document.${fileType}`;

    // Define the request body
    const requestBody = {
        name: fileName,
        file: {},
        "@microsoft.graph.conflictBehavior": "rename"
    };

    // Define the request headers
    const headers = {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
    };

    // Make the API request
    const response = await fetch(baseUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
    });

    // Check for errors
    if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to create file: ${error.error.message}`);
    }

    // Parse and return the response
    const data = await response.json();
    console.log('File created:', data);
    return data;
}

export async function cleanupTrash(accessToken) {
    const fitdocId = await getOrCreateFitdocFolder(accessToken)
    const trashFolderId = await getOrCreateTrashFolder(accessToken, fitdocId)
    // Get all items in the "Trash" folder
    const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${trashFolderId}/children`, {
        headers: {
            'Authorization': `Bearer ${accessToken}`
        }
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error ? data.error.message : 'Unknown error occurred');
    }

    const trashItems = await response.json();

    // Loop through each item and delete it
    for (const item of trashItems.value) {
        // Use the item name as the original file ID
        const originalFileId = item.name;

        try {
            // Attempt to delete the original file
            const deleteResponse = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${originalFileId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!deleteResponse.ok) {
                // If the original file cannot be deleted, log the error
                const deleteError = await deleteResponse.json();
                console.error(`Failed to delete original file ${originalFileId}: ${deleteError.error.message}`);
                continue;
            }

            // Delete the item from the Trash folder
            await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${item.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            console.log(`Deleted original item ${originalFileId} and its copy in Trash.`);

        } catch (error) {
            console.error(`Failed to delete item with original ID ${originalFileId} or its copy in Trash:`, error.message);
        }
    }

    console.log('Trash folder cleanup completed.');
}

export async function moveToTrash(accessToken, itemId) {
    try {
        const fitdocId = await getOrCreateFitdocFolder(accessToken)
        const trashId = await getOrCreateTrashFolder(accessToken, fitdocId)
        const copy = await copyFileToTrash(accessToken, itemId, trashId)
        console.log("Item copied to trash folder")
    } catch (e) {
        console.log(e);

    }
}

async function getOrCreateFitdocFolder(accessToken) {
    const folderName = CONFIG.onedrive.fitdoc_foldername;

    // Check if "__FITDOC__" folder exists in the root
    const response = await fetch('https://graph.microsoft.com/v1.0/me/drive/root/children', {
        headers: {
            'Authorization': `Bearer ${accessToken}`
        }
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error ? data.error.message : 'Unknown error occurred');
    }

    const items = await response.json();
    const fitdocFolder = items.value.find(item => item.name === folderName && item.folder);

    if (fitdocFolder) {
        return fitdocFolder.id;
    } else {
        // Create "__FITDOC__" folder in the root
        const createResponse = await fetch('https://graph.microsoft.com/v1.0/me/drive/root/children', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                name: folderName,
                folder: {},
                '@microsoft.graph.conflictBehavior': 'rename'
            })
        });

        if (!createResponse.ok) {
            const data = await createResponse.json();
            throw new Error(data.error ? data.error.message : 'Unknown error occurred');
        }

        const newFolder = await createResponse.json();
        return newFolder.id;
    }
}

async function getOrCreateTrashFolder(accessToken, parentFolderId) {
    const folderName = CONFIG.onedrive.fitdoc_trash_foldername;

    // List all items in the parent folder to find the "Trash" folder
    const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}/children`, {
        headers: {
            'Authorization': `Bearer ${accessToken}`
        }
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error ? data.error.message : 'Unknown error occurred');
    }

    const items = await response.json();
    const trashFolder = items.value.find(item => item.name === folderName && item.folder);

    if (trashFolder) {
        return trashFolder.id;
    } else {
        // Create the "Trash" folder
        const createResponse = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}/children`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                name: folderName,
                folder: {},
                '@microsoft.graph.conflictBehavior': 'rename'
            })
        });

        if (!createResponse.ok) {
            const data = await createResponse.json();
            throw new Error(data.error ? data.error.message : 'Unknown error occurred');
        }

        const newFolder = await createResponse.json();
        return newFolder.id;
    }
}

async function copyFileToTrash(accessToken, itemId, trashFolderId) {
    const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${itemId}/copy`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            parentReference: {
                id: trashFolderId
            },
            name:`${itemId}`
        })
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error ? data.error.message : 'Unknown error occurred');
    }

    console.log('File copy initiated. Monitor the status via the OneDrive UI.');

    return true

}